import {
  OBJECT_TYPE_ACL_TEMPLATES,
  OBJECT_TYPE_ASSETS,
  OBJECT_TYPE_COLLECTIONS,
  OBJECT_TYPE_CUSTOM_ACTIONS,
  OBJECT_TYPE_EXPORT_LOCATIONS,
  OBJECT_TYPE_GROUPS,
  OBJECT_TYPE_METADATA_VIEWS,
  OBJECT_TYPE_PLAYLISTS,
  OBJECT_TYPE_PORTALS,
  OBJECT_TYPE_SAVED_SEARCHES,
  OBJECT_TYPE_SEARCH_VIEWS,
  OBJECT_TYPE_STORAGES,
  OBJECT_TYPE_TRANSCODERS,
  OBJECT_TYPE_USERS,
} from '@iconik-io/iconik-api/src/types/object_types';
import type { UserRoles } from '@iconik-io/shared-lib/src/utils/roles';

import type { CommonAclsPrefix } from '../ducks/common_acls/types';
import type { PermissionValues } from '../types/common';

type EntityRoles = Record<'read' | 'write' | 'delete', boolean>;

export const getEntityUserRoles = (
  userRoles: UserRoles,
): Record<CommonAclsPrefix, EntityRoles> => ({
  [OBJECT_TYPE_TRANSCODERS]: {
    read: userRoles.canReadTranscoders,
    write: userRoles.canWriteTranscoders,
    delete: userRoles.canDeleteTranscoders,
  },
  [OBJECT_TYPE_EXPORT_LOCATIONS]: {
    read: userRoles.canReadExportLocations,
    write: userRoles.canWriteExportLocations,
    delete: userRoles.canDeleteExportLocations,
  },
  [OBJECT_TYPE_USERS]: {
    read: userRoles.canReadUsers,
    write: userRoles.canWriteUsers,
    delete: userRoles.canDeleteUsers,
  },
  [OBJECT_TYPE_GROUPS]: {
    read: userRoles.canReadGroups,
    write: userRoles.canWriteGroups,
    delete: userRoles.canDeleteGroups,
  },
  [OBJECT_TYPE_STORAGES]: {
    read: userRoles.canReadStorages,
    write: userRoles.canWriteStorages,
    delete: userRoles.canDeleteStorages,
  },
  [OBJECT_TYPE_SAVED_SEARCHES]: {
    read: userRoles.canReadSavedSearches,
    write: userRoles.canWriteSavedSearches,
    delete: userRoles.canDeleteSavedSearches,
  },
  [OBJECT_TYPE_ASSETS]: {
    read: userRoles.canReadAssets,
    write: userRoles.canWriteAssets,
    delete: userRoles.canDeleteAssets,
  },
  [OBJECT_TYPE_COLLECTIONS]: {
    read: userRoles.canReadCollections,
    write: userRoles.canWriteCollections,
    delete: userRoles.canDeleteCollections,
  },
  [OBJECT_TYPE_METADATA_VIEWS]: {
    read: userRoles.canReadMetadataViews,
    write: userRoles.canWriteMetadataValues,
    delete: userRoles.canDeleteMetadataViews,
  },
  [OBJECT_TYPE_SEARCH_VIEWS]: {
    read: userRoles.canReadSearchViews,
    write: userRoles.canWriteSearchViews,
    delete: userRoles.canDeleteSearchViews,
  },
  [OBJECT_TYPE_CUSTOM_ACTIONS]: {
    read: userRoles.canReadCustomActions,
    write: false, // admin only
    delete: false, // admin only
  },
  [OBJECT_TYPE_ACL_TEMPLATES]: {
    read: userRoles.canReadAclTemplates,
    write: userRoles.canWriteAclTemplates,
    delete: userRoles.canDeleteAclTemplates,
  },
  [OBJECT_TYPE_PLAYLISTS]: {
    read: userRoles.canReadPlaylists,
    write: userRoles.canWritePlaylists,
    delete: userRoles.canDeletePlaylists,
  },
  [OBJECT_TYPE_PORTALS]: {
    read: userRoles.canReadPortals,
    write: userRoles.canWritePortals,
    delete: userRoles.canDeletePortals,
  },
});

export const getPermissionsRolesMerge = (
  aclsType: CommonAclsPrefix,
  userRoles: UserRoles,
  entityPermissions: PermissionValues | undefined,
  entityUserRoles: EntityRoles,
): PermissionValues | undefined => {
  if (!entityPermissions) {
    return undefined;
  }
  const permissionsRolesMerge: PermissionValues = {
    read: entityPermissions.read && entityUserRoles.read,
    write: entityPermissions.write && entityUserRoles.write,
    delete: entityPermissions.delete && entityUserRoles.delete,
    'change-acl': entityPermissions['change-acl'] && userRoles.canWriteAcls,
  };

  // The canEditAllUsers and canEditAllGroups role exists to allow overriding the ACLs for users and groups.
  // This is to avoid having to add read and write ACLs for user admins when new users are added.
  // See https://linear.app/iconik/issue/FE-1736/support-the-new-edit-users-role
  if (
    (aclsType === OBJECT_TYPE_USERS && userRoles.canEditAllUsers) ||
    (aclsType === OBJECT_TYPE_GROUPS && userRoles.canEditAllGroups)
  ) {
    return {
      ...permissionsRolesMerge,
      read: true,
      write: true,
      delete: true,
    };
  }

  return permissionsRolesMerge;
};
